import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AssignBadgeResponse,
  Badge,
  BadgeAssignedEnvelope,
  BadgeCreatedEnvelope,
  BadgeDeletedEnvelope,
  BadgeUnassignedEnvelope,
  BadgeUpdatedEnvelope,
  GetMemberCountsPerBadgeResponse,
  ListBadgesOptions,
  ListBadgesResponse,
  ListMembersBadgeIdsResponse,
  ListMembersByBadgeOptions,
  ListMembersResponse,
  UpdateBadge,
  UpdateBadgesDisplayOrderResponse,
  assignBadge as universalAssignBadge,
  createBadge as universalCreateBadge,
  deleteBadge as universalDeleteBadge,
  getBadge as universalGetBadge,
  getMemberCountsPerBadge as universalGetMemberCountsPerBadge,
  listBadges as universalListBadges,
  listBadgesPerMember as universalListBadgesPerMember,
  listMembersByBadge as universalListMembersByBadge,
  unassignBadge as universalUnassignBadge,
  updateBadge as universalUpdateBadge,
  updateBadgesDisplayOrder as universalUpdateBadgesDisplayOrder,
} from './badges-v3-badge-badges.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/members' };

type BadgeNonNullablePaths = `_id`;

export function createBadge(httpClient: HttpClient): CreateBadgeSignature {
  return (
    badge: NonNullablePaths<Badge, `backgroundColor` | `textColor` | `title`>
  ) =>
    universalCreateBadge(
      badge,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateBadgeSignature {
  /**
   * Creates a badge.
   *
   * New badges do not have any badge permissions by default. Wix users can set badge permissions from the [Badges page in the dashboard](https://support.wix.com/en/article/site-members-creating-and-managing-member-badges?tabs=Dashboard-0#managing-member-badges).
   *
   * If `backgroundColor` or `textColor` are not specified, they default to `"#796EFF"` (purple) and `"#FFFFFF"` (white) respectively.
   * @param - Badge to create.
   * @returns Created badge.
   */
  (
    badge: NonNullablePaths<Badge, `backgroundColor` | `textColor` | `title`>
  ): Promise<NonNullablePaths<Badge, BadgeNonNullablePaths>>;
}

export function updateBadge(httpClient: HttpClient): UpdateBadgeSignature {
  return (_id: string, badge: UpdateBadge) =>
    universalUpdateBadge(
      _id,
      badge,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateBadgeSignature {
  /**
   * Updates a badge.
   *
   * Note that the badge `slug` is based on the badge `title`. If you update the `title`, the `slug` updates accordingly.
   * @param - Badge ID.
   * @returns Updated badge.
   */
  (_id: string, badge: UpdateBadge): Promise<
    NonNullablePaths<Badge, BadgeNonNullablePaths>
  >;
}

export function listBadges(httpClient: HttpClient): ListBadgesSignature {
  return (options?: ListBadgesOptions) =>
    universalListBadges(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListBadgesSignature {
  /**
   * Retrieves up to 1,000 badges, given the requested paging.
   *
   * Default `paging.limit` is `100` and `paging.offset` is `0`.
   *
   * This method is not universal and only runs on the backend.
   */
  (options?: ListBadgesOptions): Promise<
    NonNullablePaths<
      ListBadgesResponse,
      {
        [P in BadgeNonNullablePaths]: `badges.${number}.${P}`;
      }[BadgeNonNullablePaths]
    >
  >;
}

export function getBadge(httpClient: HttpClient): GetBadgeSignature {
  return (_id: string) =>
    universalGetBadge(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetBadgeSignature {
  /**
   * Retrieves a badge.
   *
   * This method is not universal and only runs on the backend.
   * @param - Badge ID.
   * @returns Badge.
   */
  (_id: string): Promise<NonNullablePaths<Badge, BadgeNonNullablePaths>>;
}

export function deleteBadge(httpClient: HttpClient): DeleteBadgeSignature {
  return (_id: string) =>
    universalDeleteBadge(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteBadgeSignature {
  /**
   * Deletes a badge.
   * @param - Badge ID.
   */
  (_id: string): Promise<void>;
}

export function assignBadge(httpClient: HttpClient): AssignBadgeSignature {
  return (_id: string, memberIds: string[]) =>
    universalAssignBadge(
      _id,
      memberIds,
      // @ts-ignore
      { httpClient }
    );
}

interface AssignBadgeSignature {
  /**
   * Assigns a badge to site members.
   *
   * Members inherit the badge's permissions when they receive the badge.
   * Badge permissions are added to previous member permissions, they don't replace the existing permissions.
   * @param - Badge ID.
   * @param - Member IDs to assign to the badge.
   */
  (_id: string, memberIds: string[]): Promise<
    NonNullablePaths<AssignBadgeResponse, `memberIds`>
  >;
}

export function unassignBadge(httpClient: HttpClient): UnassignBadgeSignature {
  return (_id: string, memberIds: string[]) =>
    universalUnassignBadge(
      _id,
      memberIds,
      // @ts-ignore
      { httpClient }
    );
}

interface UnassignBadgeSignature {
  /**
   * Removes an assigned badge from multiple site members.
   *
   * The members lose the badge's permissions when the badge is removed.
   * @param - Badge ID.
   * @param - List of members to remove.
   */
  (_id: string, memberIds: string[]): Promise<void>;
}

export function listMembersByBadge(
  httpClient: HttpClient
): ListMembersByBadgeSignature {
  return (_id: string, options?: ListMembersByBadgeOptions) =>
    universalListMembersByBadge(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListMembersByBadgeSignature {
  /**
   * Retrieves up to 1,000 site members assigned to a badge.
   *
   * Default `paging.limit` is `100` and `paging.offset` is `0`.
   *
   * This method is not universal and only runs on the backend.
   * @param - Badge ID.
   */
  (_id: string, options?: ListMembersByBadgeOptions): Promise<
    NonNullablePaths<ListMembersResponse, `memberIds`>
  >;
}

export function listBadgesPerMember(
  httpClient: HttpClient
): ListBadgesPerMemberSignature {
  return (memberIds: string[]) =>
    universalListBadgesPerMember(
      memberIds,
      // @ts-ignore
      { httpClient }
    );
}

interface ListBadgesPerMemberSignature {
  /**
   * Retrieves badges assigned to specified members.
   *
   * Members are typically associated with a contact, each having a distinct `memberId` and `contactId`. When passing the ID as a parameter, avoid presuming the IDs are identical since they represent separate entities.
   *
   * This method is not universal and only runs on the backend.
   * @param - Member IDs to retrieve badges for.
   */
  (memberIds: string[]): Promise<
    NonNullablePaths<
      ListMembersBadgeIdsResponse,
      `memberBadgeIds` | `memberBadgeIds.${number}.memberId`
    >
  >;
}

export function getMemberCountsPerBadge(
  httpClient: HttpClient
): GetMemberCountsPerBadgeSignature {
  return () =>
    universalGetMemberCountsPerBadge(
      // @ts-ignore
      { httpClient }
    );
}

interface GetMemberCountsPerBadgeSignature {
  /**
   * Retrieves a member count per badge.
   *
   * This method is not universal function and only runs on the backend.
   */
  (): Promise<
    NonNullablePaths<
      GetMemberCountsPerBadgeResponse,
      | `badgeMemberCounts`
      | `badgeMemberCounts.${number}.badgeId`
      | `badgeMemberCounts.${number}.memberCount`
    >
  >;
}

export function updateBadgesDisplayOrder(
  httpClient: HttpClient
): UpdateBadgesDisplayOrderSignature {
  return (badgeIds: string[]) =>
    universalUpdateBadgesDisplayOrder(
      badgeIds,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateBadgesDisplayOrderSignature {
  /**
   * Updates the display order of badges.
   *
   * This method is not universal function and only runs on the backend
   * @param - Badge IDs in the order to display.
   */
  (badgeIds: string[]): Promise<
    NonNullablePaths<
      UpdateBadgesDisplayOrderResponse,
      {
        [P in BadgeNonNullablePaths]: `badges.${number}.${P}`;
      }[BadgeNonNullablePaths]
    >
  >;
}

export const onBadgeAssigned = EventDefinition(
  'wix.badges.v3.badge_badge_assigned',
  true,
  (event: BadgeAssignedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<BadgeAssignedEnvelope>();
export const onBadgeUnassigned = EventDefinition(
  'wix.badges.v3.badge_badge_unassigned',
  true,
  (event: BadgeUnassignedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<BadgeUnassignedEnvelope>();
export const onBadgeCreated = EventDefinition(
  'wix.badges.v3.badge_created',
  true,
  (event: BadgeCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BadgeCreatedEnvelope>();
export const onBadgeDeleted = EventDefinition(
  'wix.badges.v3.badge_deleted',
  true,
  (event: BadgeDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BadgeDeletedEnvelope>();
export const onBadgeUpdated = EventDefinition(
  'wix.badges.v3.badge_updated',
  true,
  (event: BadgeUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BadgeUpdatedEnvelope>();

export {
  ActionEvent,
  AssignBadgeRequest,
  AssignBadgeResponse,
  AssignBadgesRequest,
  AssignBadgesResponse,
  Badge,
  BadgeAssigned,
  BadgeAssignedEnvelope,
  BadgeCreatedEnvelope,
  BadgeDeletedEnvelope,
  BadgeMemberCount,
  BadgeUnassigned,
  BadgeUnassignedEnvelope,
  BadgeUpdatedEnvelope,
  BaseEventMetadata,
  CountBadgesRequest,
  CountBadgesResponse,
  CreateBadgeRequest,
  CreateBadgeResponse,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  DeleteBadgeRequest,
  DeleteBadgeResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetBadgeRequest,
  GetBadgeResponse,
  GetMemberCountsPerBadgeRequest,
  GetMemberCountsPerBadgeResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListBadgesOptions,
  ListBadgesRequest,
  ListBadgesResponse,
  ListMembersBadgeIdsRequest,
  ListMembersBadgeIdsResponse,
  ListMembersByBadgeOptions,
  ListMembersRequest,
  ListMembersResponse,
  ListMembersWithBadgesRequest,
  ListMembersWithBadgesResponse,
  MemberBadgeIds,
  MessageEnvelope,
  Paging,
  PagingMetadata,
  QueryBadgesRequest,
  QueryBadgesResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  UnassignBadgeRequest,
  UnassignBadgeResponse,
  UpdateBadge,
  UpdateBadgeRequest,
  UpdateBadgeResponse,
  UpdateBadgesDisplayOrderRequest,
  UpdateBadgesDisplayOrderResponse,
  WebhookIdentityType,
} from './badges-v3-badge-badges.universal.js';
